<template>
  <!-- Pelkkä select, ettei mobiilinäppäimistö aukea kenttää napautettaessa.
       Suodatus tehdään valikon yläreunassa olevalla hakukentällä. -->
  <v-select
    ref="select"
    v-bind="selectAttrs"
    class="searchable-select"
    :attach="$vuetify.breakpoint.xsOnly"
    disable-lookup
    :items="filteredItems"
    :value="value"
    v-on="$listeners"
  >
    <template v-slot:prepend-item>
      <div class="searchable-select-search">
        <v-list-item>
          <v-text-field
            ref="searchField"
            v-model="search"
            :placeholder="searchPlaceholder || $t('search')"
            clearable
            dense
            filled
            rounded
            hide-details
            prepend-inner-icon="mdi-magnify"
            @focus="scrollToTop"
          />
        </v-list-item>
        <!-- Kutsujan lisäsuodattimet (esim. nimiketyypit) hakukentän alle. -->
        <slot name="filters" />
        <v-divider class="mt-2" />
      </div>
    </template>
    <!-- Tavalliset slotit välitetään ilman scopea, jotta Vuetify löytää ne $slots-kokoelmasta (esim. prepend). -->
    <template v-for="plainName in plainSlots()" v-slot:[plainName]>
      <slot :name="plainName" />
    </template>
    <template v-for="scopedName in scopedSlots()" v-slot:[scopedName]="scope">
      <slot :name="scopedName" v-bind="scope" />
    </template>
  </v-select>
</template>

<script>
import { matchesWords, searchWords } from '@/helpers/textSearch';

// Slotit, joita ei välitetä v-selectille.
const ownSlots = ['prepend-item', 'filters'];

export default {
  name: 'SearchableSelect',
  inheritAttrs: false,
  props: {
    value: {
      type: [Object, Array, String, Number],
      default: null,
    },
    items: {
      type: Array,
      default: () => [],
    },
    // Palauttaa tekstin, johon hakusanaa verrataan.
    searchText: {
      type: Function,
      default: item => item.name || '',
    },
    searchPlaceholder: String,
  },
  data() {
    return {
      search: '',
    };
  },
  mounted() {
    // autofocus hoidetaan itse: v-selectille annettuna Vuetify fokusoisi kentän uudelleen (IntersectionObserver)
    // ja veisi fokuksen hakukentältä. Tyhjä kenttä avaa työpöydällä valikon heti, jolloin haku alkaa ilman klikkausta.
    if (this.autofocus) {
      this.$nextTick(() => {
        if (this.value == null && !this.$vuetify.breakpoint.xsOnly) this.$refs.select.activateMenu();
        else this.$refs.select.focus();
      });
    }
    // isContentActive muuttuu vasta, kun valikon sisältö on näkyvissä; isMenuActive on tosi jo ennen
    // ensimmäistä renderöintiä, jolloin piilossa olevaa hakukenttää ei voi fokusoida.
    this.$watch(
      () => this.$refs.select.$refs.menu.isContentActive,
      active => {
        this.$emit('menu', active);
        if (!active) {
          this.search = '';
          return;
        }
        // Työpöydällä kirjoittaminen alkaa heti; mobiilissa näppäimistö ei saa aueta kenttää napauttamalla.
        if (!this.$vuetify.breakpoint.xsOnly) {
          this.$nextTick(() => this.$refs.searchField && this.$refs.searchField.focus());
        }
      }
    );
  },
  computed: {
    autofocus() {
      return this.$attrs.autofocus !== undefined && this.$attrs.autofocus !== false;
    },
    selectAttrs() {
      const { autofocus, ...attrs } = this.$attrs; // eslint-disable-line no-unused-vars
      return attrs;
    },
    filteredItems() {
      const words = searchWords(this.search);
      if (!words.length) return this.items;

      // Valitut pidetään aina mukana, muuten v-select ei näytä valintaa suodatuksen aikana.
      const key = this.$attrs['item-value'] || this.$attrs.itemValue || 'value';
      const values = Array.isArray(this.value) ? this.value : this.value != null ? [this.value] : [];
      const selected = values.map(v => (v && typeof v === 'object' ? v[key] : v));

      // Otsikot ja jakajat otetaan mukaan vain, jos niiden jälkeen tulee osuma.
      const result = [];
      let pending = [];
      this.items.forEach(item => {
        if (item && item.header !== undefined) {
          pending = pending.filter(i => i.divider);
          pending.push(item);
          return;
        }
        if (item && item.divider) {
          pending.push(item);
          return;
        }
        const matches = selected.includes(item[key]) || matchesWords(this.searchText(item), words);
        if (!matches) return;
        if (!result.length) pending = pending.filter(i => !i.divider);
        result.push(...pending, item);
        pending = [];
      });
      return result;
    },
  },
  methods: {
    scrollToTop() {
      // Mobiilissa näppäimistö peittää alaosan: vieritetään kenttä yläreunaan, jotta valikolle jää tilaa.
      if (this.$vuetify.breakpoint.xsOnly) {
        this.$el.scrollIntoView({ block: 'start' });
      }
    },
    plainSlots() {
      return Object.keys(this.$slots).filter(name => !ownSlots.includes(name));
    },
    scopedSlots() {
      return Object.keys(this.$scopedSlots).filter(name => !ownSlots.includes(name) && !this.$slots[name]);
    },
  },
};
</script>

<style scoped>
/* Fokuksessa kenttä vieritetään yläreunaan, mutta jätetään pieni rako reunaan. */
.searchable-select {
  scroll-margin-top: 12px;
}

/* Hakukenttä pysyy valikon yläreunassa listaa vieritettäessä.
   Negatiivinen marginaali kattaa v-listin 8px yläpehmusteen, ettei kenttä liikahda kun se vierittyy pois. */
.searchable-select-search {
  position: sticky;
  top: 0;
  z-index: 1;
  background: inherit;
  margin-top: -8px;
  padding-top: 8px;
}
</style>
